import { createTheme } from "@mui/material/styles";
import { ruRU as coreRuRU } from "@mui/material/locale";
import { ruRU as dataGridRuRU } from "@mui/x-data-grid/locales";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { ruRU as pickersRuRU } from "@mui/x-date-pickers/locales";
import { MRT_Localization_RU } from "material-react-table/locales/ru";

/** Brand book 02 "Potoki" — tokens from epfc/docs/design */
export const brand = {
  navy: "#19286d",
  govBlue: "#0068b4",
  ink: "#17203a",
  muted: "#5a6780",
  line: "#d7e0ec",
  bg: "#f5f8fc",
  white: "#ffffff",
  font: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
} as const;

/** Общие токены таблиц: DataGrid и Material React Table выглядят одинаково */
export const tableTokens = {
  border: brand.line,
  headerText: brand.muted,
  headerFontSize: 13,
  headerFontWeight: 600,
  cellFontSize: 13,
  zebra: "rgba(23, 32, 58, 0.025)",
  rowHover: "rgba(0, 104, 180, 0.06)",
  rowSelected: "rgba(25, 40, 109, 0.08)",
  rowSelectedHover: "rgba(25, 40, 109, 0.12)",
  radius: 8,
} as const;

const navySelected = "rgba(25, 40, 109, 0.08)";
const navySelectedHover = "rgba(25, 40, 109, 0.12)";
/** Same weight as MUI outlined input (`rgba(0,0,0,0.23)`), tinted with brand ink */
const outline = "rgba(23, 32, 58, 0.23)";
const tableLine = tableTokens.border;
const zebra = tableTokens.zebra;
const rowHover = tableTokens.rowHover;
const rowSelected = tableTokens.rowSelected;
const rowSelectedHover = tableTokens.rowSelectedHover;

export const appTheme = createTheme(
  {
    palette: {
      primary: {
        main: brand.navy,
        dark: brand.govBlue,
        contrastText: brand.white,
      },
      text: {
        primary: brand.ink,
        secondary: brand.muted,
      },
      divider: brand.line,
      background: {
        default: brand.bg,
        paper: brand.white,
      },
      action: {
        selected: navySelected,
        hover: "rgba(0, 104, 180, 0.08)",
        focus: "rgba(0, 104, 180, 0.16)",
      },
    },
    typography: {
      fontFamily: brand.font,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      button: {
        fontWeight: 600,
        textTransform: "none",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: brand.bg,
            color: brand.ink,
            fontFamily: brand.font,
          },
          a: {
            color: brand.govBlue,
          },
          ".MuiDataGrid-root": {
            backgroundColor: brand.white,
            border: `1px solid ${tableLine}`,
            borderRadius: tableTokens.radius,
            color: brand.ink,
            fontSize: tableTokens.cellFontSize,
            "--DataGrid-rowBorderColor": tableLine,
            "--DataGrid-containerBackground": brand.white,
          },
          ".MuiDataGrid-withBorderColor": {
            borderColor: `${tableLine} !important`,
          },
          ".MuiDataGrid-columnHeaders, .MuiDataGrid-columnHeader, .MuiDataGrid-filler, .MuiDataGrid-pinnedColumnHeaders":
            {
              backgroundColor: `${brand.white} !important`,
              color: tableTokens.headerText,
              borderColor: `${tableLine} !important`,
              fontSize: tableTokens.headerFontSize,
              fontWeight: tableTokens.headerFontWeight,
            },
          ".MuiDataGrid-cell": {
            borderColor: `${tableLine} !important`,
            color: brand.ink,
            backgroundColor: "transparent",
          },
          ".MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: zebra,
          },
          ".MuiDataGrid-row:hover, .MuiDataGrid-row.Mui-hovered": {
            backgroundColor: rowHover,
          },
          ".MuiDataGrid-row:nth-of-type(even):hover, .MuiDataGrid-row:nth-of-type(even).Mui-hovered":
            {
              backgroundColor: rowHover,
            },
          ".MuiDataGrid-row.Mui-selected": {
            backgroundColor: rowSelected,
          },
          ".MuiDataGrid-row.Mui-selected:hover, .MuiDataGrid-row.Mui-selected.Mui-hovered":
            {
              backgroundColor: rowSelectedHover,
            },
          ".MuiDataGrid-footerContainer": {
            backgroundColor: brand.white,
            borderColor: `${tableLine} !important`,
          },
          ".MuiDataGrid-columnSeparator": {
            color: tableLine,
          },
          ".MuiDataGrid-overlay, .MuiDataGrid-virtualScroller": {
            backgroundColor: brand.white,
          },
          ".MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within":
            {
              outline: "none",
            },
          ".MuiTable-root": {
            backgroundColor: brand.white,
            color: brand.ink,
          },
          ".MuiTableContainer-root.MuiPaper-root": {
            backgroundColor: brand.white,
            backgroundImage: "none",
            boxShadow: "none",
            border: `1px solid ${tableLine}`,
            borderRadius: tableTokens.radius,
          },
          ".MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: `${brand.white} !important`,
            color: tableTokens.headerText,
            borderColor: tableLine,
            fontSize: tableTokens.headerFontSize,
            fontWeight: tableTokens.headerFontWeight,
          },
          ".MuiTableCell-root": {
            borderColor: tableLine,
            color: brand.ink,
          },
          ".MuiTableBody-root .MuiTableCell-root": {
            fontSize: tableTokens.cellFontSize,
          },
          ".MuiTableBody-root .MuiTableRow-root:nth-of-type(even) > td": {
            backgroundColor: zebra,
          },
          ".MuiTableBody-root .MuiTableRow-root:hover > td": {
            backgroundColor: `${rowHover} !important`,
          },
          ".MuiTableBody-root .MuiTableRow-root.Mui-selected > td": {
            backgroundColor: rowSelected,
          },
          ".MuiTablePagination-root": {
            backgroundColor: brand.white,
            color: tableTokens.headerText,
            borderColor: tableLine,
          },
          "*": {
            scrollbarWidth: "thin",
            scrollbarColor: `${brand.line} transparent`,
          },
          "*::-webkit-scrollbar": {
            width: 8,
            height: 8,
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: brand.line,
            borderRadius: 8,
            border: "2px solid transparent",
            backgroundClip: "padding-box",
          },
          "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: brand.muted,
          },
          "*::-webkit-scrollbar-thumb:active": {
            backgroundColor: brand.navy,
          },
          "*::-webkit-scrollbar-corner": {
            backgroundColor: "transparent",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
            "&.Mui-focusVisible": {
              outline: `2px solid ${brand.govBlue}`,
              outlineOffset: 2,
            },
          },
          contained: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          },
          containedPrimary: {
            backgroundColor: brand.navy,
            "&:hover": {
              backgroundColor: brand.govBlue,
              boxShadow: "none",
            },
          },
          outlined: {
            borderColor: brand.line,
            "&:hover": {
              borderColor: brand.govBlue,
              backgroundColor: "rgba(0, 104, 180, 0.08)",
              color: brand.govBlue,
            },
          },
          outlinedPrimary: {
            borderColor: brand.navy,
            color: brand.navy,
            "&:hover": {
              borderColor: brand.govBlue,
              backgroundColor: "rgba(0, 104, 180, 0.08)",
              color: brand.govBlue,
            },
          },
          text: {
            "&:hover": {
              backgroundColor: "rgba(0, 104, 180, 0.08)",
              color: brand.govBlue,
            },
          },
          textPrimary: {
            color: brand.navy,
            "&:hover": {
              backgroundColor: "rgba(0, 104, 180, 0.08)",
              color: brand.govBlue,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "rgba(0, 104, 180, 0.08)",
              color: brand.govBlue,
              "& .MuiSvgIcon-root": {
                color: brand.govBlue,
              },
            },
          },
          colorPrimary: {
            color: brand.navy,
            "&:hover": {
              backgroundColor: "rgba(0, 104, 180, 0.12)",
              color: brand.govBlue,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            "&:hover": {
              color: brand.govBlue,
              backgroundColor: "rgba(0, 104, 180, 0.06)",
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "rgba(0, 104, 180, 0.08)",
            },
            "&.Mui-selected": {
              backgroundColor: navySelected,
              "&:hover": {
                backgroundColor: navySelectedHover,
              },
            },
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "rgba(0, 104, 180, 0.08)",
            },
            "&.Mui-selected": {
              backgroundColor: navySelected,
              color: brand.navy,
              "&:hover": {
                backgroundColor: navySelectedHover,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          clickable: {
            "&:hover": {
              backgroundColor: "rgba(0, 104, 180, 0.12)",
            },
          },
          clickableColorPrimary: {
            "&:hover": {
              backgroundColor: brand.govBlue,
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: brand.govBlue,
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          showCellVerticalBorder: false,
          showColumnVerticalBorder: false,
        },
        styleOverrides: {
          root: {
            border: `1px solid ${tableLine}`,
            borderColor: tableLine,
            "--DataGrid-rowBorderColor": tableLine,
          },
          withBorderColor: {
            borderColor: tableLine,
          },
          cell: {
            borderColor: tableLine,
            backgroundColor: "transparent",
          },
          row: {
            "&:nth-of-type(even)": {
              backgroundColor: zebra,
            },
            "&:hover, &.Mui-hovered": {
              backgroundColor: rowHover,
            },
            "&:nth-of-type(even):hover, &:nth-of-type(even).Mui-hovered": {
              backgroundColor: rowHover,
            },
            "&.Mui-selected": {
              backgroundColor: rowSelected,
            },
            "&.Mui-selected:hover, &.Mui-selected.Mui-hovered": {
              backgroundColor: rowSelectedHover,
            },
          },
          columnHeaders: {
            borderColor: tableLine,
          },
          columnHeader: {
            borderColor: tableLine,
          },
          footerContainer: {
            borderColor: tableLine,
          },
          columnSeparator: {
            color: tableLine,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: outline,
          },
          root: {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: brand.govBlue,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: brand.govBlue,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: navySelected,
              color: brand.navy,
              "&:hover": {
                backgroundColor: navySelectedHover,
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: tableLine,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: brand.line,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          outlined: {
            borderColor: brand.line,
          },
        },
      },
    },
  },
  coreRuRU,
  dataGridRuRU,
  pickersRuRU
);

export const mrtLocalizationRu = MRT_Localization_RU;

/** Тема Material React Table: те же цвета, что у DataGrid */
export const mrtBrandTheme = {
  baseBackgroundColor: brand.white,
  draggingBorderColor: tableLine,
  pinnedRowBackgroundColor: "rgba(25, 40, 109, 0.04)",
  selectedRowBackgroundColor: rowSelected,
};

export const mrtBrandPaperProps = {
  elevation: 0,
  sx: {
    backgroundColor: brand.white,
    border: `1px solid ${tableLine}`,
    borderRadius: `${tableTokens.radius}px`,
    boxShadow: "none",
  },
};
